import { Select, Tag } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import odxAdminApiService from 'service/odxAdminApiService';

type ChannelOption = { value: number; label: string };
type Service = 'odk' | 'amasian';

interface Props {
  value?: number | null;
  onChange: (v: number | null) => void;
  /** 어느 서비스의 채널 목록을 가져올지. PRD v1.4 #19: 서비스별 인증 분기. */
  service?: Service;
  style?: React.CSSProperties;
}

/**
 * BEE-5023 (PRD v1.4 #19) — subtype='live' + cta_type='internal' 일 때 표시되는
 * channel id picker. 서비스(ODK/Amasian) admin API 에서 채널 목록을 fetch.
 *
 * 채널 모델은 mediahub 에 없어서 cta_channel_id 는 raw int 로 ShortsMeta 에
 * 저장. service prop 으로 어떤 서비스의 채널인지 결정.
 *
 * admin API 가 비활성/네트워크 실패 시 channels 가 비어있으면 fallback 으로
 * raw int 입력을 그대로 노출 (운영자가 직접 입력 가능).
 */
const LiveChannelPicker: React.FC<Props> = ({
  value,
  onChange,
  service = 'odk',
  style,
}) => {
  const [channels, setChannels] = useState<ChannelOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const fetcher =
      service === 'amasian'
        ? odxAdminApiService.fetchAmasianChannels
        : odxAdminApiService.fetchOdkChannels;
    fetcher()
      .then(setChannels)
      .finally(() => setLoading(false));
  }, [service]);

  const selectedLabel = useMemo(() => {
    if (value == null) return null;
    const hit = channels.find(c => c.value === value);
    return hit?.label ?? null;
  }, [value, channels]);

  if (!loading && channels.length === 0) {
    // fallback — admin API 비활성. 운영자가 raw int 입력.
    return (
      <div style={style}>
        <input
          type="number"
          min={1}
          value={value ?? ''}
          onChange={e => {
            const n = Number(e.target.value);
            onChange(Number.isFinite(n) && n > 0 ? n : null);
          }}
          style={{ width: 200, padding: 4, border: '1px solid #d9d9d9', borderRadius: 4 }}
        />
        <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
          채널 목록 fetch 실패 — channel id 를 직접 입력하세요.
        </div>
      </div>
    );
  }

  return (
    <div style={style}>
      <Select<number>
        showSearch
        allowClear
        loading={loading}
        placeholder={`${service.toUpperCase()} 채널 선택`}
        style={{ width: '100%', minWidth: 280 }}
        value={value ?? undefined}
        onChange={v => onChange(v ?? null)}
        options={channels}
        optionFilterProp="label"
        filterOption={(input, option) =>
          String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
        }
      />
      {value != null && !selectedLabel ? (
        <Tag color="orange" style={{ marginTop: 4 }}>
          기존 저장된 channel id {value} 가 목록에 없음
        </Tag>
      ) : null}
    </div>
  );
};

export default LiveChannelPicker;
